// Card.jsx - Professional card component for Foundations Risk Aperture
import React from 'react';

const Card = ({ 
  children, 
  className = '', 
  variant = 'default',
  hover = true,
  animated = true,
  onClick,
  ...props 
}) => {
  const baseClasses = 'ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.12)] transition-all duration-300';
  
  const variantClasses = {
    default: '',
    elevated: 'shadow-[0_20px_25px_rgba(0,0,0,0.4)]',
    outlined: 'border-2 shadow-[0_1px_3px_rgba(0,0,0,0.3)]',
    glass: 'ra-glass',
  };
  
  const hoverClasses = hover ? 'hover:shadow-[0_20px_25px_rgba(0,0,0,0.4)] hover:-translate-y-1 cursor-pointer' : '';
  const animatedClasses = animated ? 'animate-fadeInUp' : '';
  const clickableClasses = onClick ? 'cursor-pointer' : '';
  
  const combinedClasses = [
    baseClasses,
    variantClasses[variant],
    hoverClasses,
    animatedClasses,
    clickableClasses,
    className
  ].filter(Boolean).join(' ');

  // Keyboard accessibility for clickable cards
  const interactiveProps = onClick ? {
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(e);
      }
    }
  } : {};

  return (
    <div
      className={combinedClasses}
      onClick={onClick}
      {...interactiveProps}
      {...props}
    >
      {children}
    </div>
  );
};

const CardHeader = ({ children, className = '', ...props }) => (
  <div 
    className={`p-6 border-b border-[rgba(255,255,255,0.12)] ${className}`}
    {...props}
  >
    {children}
  </div>
);

const CardBody = ({ children, className = '', ...props }) => (
  <div 
    className={`p-6 ${className}`}
    {...props}
  >
    {children}
  </div>
);

const CardFooter = ({ children, className = '', ...props }) => (
  <div 
    className={`p-6 pt-0 ${className}`}
    {...props}
  >
    {children}
  </div>
);

const CardTitle = ({ children, icon, className = '', ...props }) => (
  <div className={`flex items-center gap-4 ${className}`} {...props}>
    {icon && (
      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xl">
        {icon}
      </div>
    )}
    <div>
      <h3 className="text-lg font-semibold text-[#e6f1ff] mb-1">
        {children}
      </h3>
    </div>
  </div>
);

const CardDescription = ({ children, className = '', ...props }) => (
  <p 
    className={`text-sm text-[#8892b0] ${className}`}
    {...props}
  >
    {children}
  </p>
);

// Export all components
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
Card.Title = CardTitle;
Card.Description = CardDescription;

export { Card };